import { Link } from '@inertiajs/react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import type { TechnologyBrand, TechnologySubnav, TechnologySubnavItem } from '@/types/technology'

/* In-section navigation for the technology section: ONE sticky bar on every
 * page of it.
 *
 * Load-bearing, not decoration. The marketing footer carries exactly ONE link
 * into this section (Company -> Technology, the hub), so this bar is the only
 * route between the hub and the pages under it.
 *
 * Left to right: the section wordmark (the way back to the hub), the four
 * audience links, a "Capabilities" disclosure menu, and a persistent "Talk to
 * us". Everything arrives from the server (`subnav` in technology_content.rb),
 * derived from PAGES `kind:` and AUDIENCES, so a new page needs no edit here.
 *
 * The current page is a change of SHADE (`wb-chosen`) plus aria-current -
 * never the accent, which stays for the call to action. When the current page
 * lives in the menu, the Capabilities button wears the same shade.
 *
 * Layers: the bar is a `--wb-z-sticky` step, the open menu a `--wb-z-popover`
 * step. The menu is NOT a child of the scrolling row: an overflow-x container
 * clips an absolutely positioned child, so button and panel share a wrapper
 * beside the row, the panel directly after its button in DOM order.
 *
 * Sticks at `--marketing-nav-offset` (application.css), not the navbar's
 * visible height - the fixed wrapper still intercepts pointer events below the
 * pill. `.marketing-shell` uses `overflow-x: clip`, which keeps sticky
 * resolving against the viewport.
 *
 * Phones: the bar wraps to two lines — wordmark, Capabilities and "Talk to us"
 * on the first, the audience row on the second, scrolling sideways inside its
 * own container (never the page). The menu is capped to the viewport width.
 */
const chip =
  'shrink-0 whitespace-nowrap rounded-full border border-transparent px-3 py-2 text-[0.8125rem] font-medium no-underline transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--wavbee-orange)] sm:px-3.5'
const idle = 'text-white/55 hover:bg-white/[0.05] hover:text-white/90'

function NavLink({ item }: { item: TechnologySubnavItem }) {
  return (
    <Link
      href={item.href}
      prefetch
      aria-current={item.current ? 'page' : undefined}
      className={`${chip} ${item.current ? 'wb-chosen' : idle}`}
    >
      {item.label}
    </Link>
  )
}

export default function TechSubnav({ items, brand }: { items: TechnologySubnav; brand: TechnologyBrand }) {
  const [open, setOpen] = useState(false)
  const menuId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false)
    if (returnFocus) buttonRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(true)
    }
    const onPress = (event: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) close(false)
    }
    // Tab out of the menu closes it without trapping focus.
    const onFocusIn = (event: FocusEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) close(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPress)
    document.addEventListener('touchstart', onPress)
    document.addEventListener('focusin', onFocusIn)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPress)
      document.removeEventListener('touchstart', onPress)
      document.removeEventListener('focusin', onFocusIn)
    }
  }, [open, close])

  const { overview, audiences, capabilities, cta } = items

  return (
    <nav
      aria-label={`${brand.name} sections`}
      className="sticky z-[var(--wb-z-sticky,30)] border-y border-white/[0.07] bg-[#070707]/90 backdrop-blur-md"
      style={{ top: 'var(--marketing-nav-offset)' }}
    >
      <div ref={rootRef} className="container relative mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 sm:flex-nowrap">
          <Link
            href={overview.href}
            aria-current={overview.current ? 'page' : undefined}
            aria-label={`${brand.name}: ${overview.label}`}
            className={`${chip} flex items-baseline gap-1.5 !px-2.5 ${overview.current ? 'wb-chosen' : idle}`}
          >
            <span
              className="text-[0.9375rem] font-bold text-white"
              style={{ fontFamily: 'var(--wavbee-font-display)', letterSpacing: '-0.02em' }}
            >
              {brand.name}
            </span>
          </Link>

          {/* The row owns the horizontal overflow so the page body never
              scrolls sideways. Flex-start, never centred: centring overflowing
              content makes the leading items unreachable. On a phone it drops
              to a line of its own, so the Capabilities button and the call to
              action stay on the first line, reachable without a swipe. */}
          <div className="order-last flex min-w-0 basis-full items-center gap-1 overflow-x-auto [scrollbar-width:none] sm:order-none sm:flex-initial sm:basis-auto [&::-webkit-scrollbar]:hidden">
            {audiences.map((item) => (
              <NavLink key={item.slug} item={item} />
            ))}
          </div>

          {/* The button and its menu sit OUTSIDE the scrolling row (an
              overflow-x container clips an absolutely positioned child), and
              the menu follows its button in DOM order: Tab from the open button
              lands on the first item, not on "Talk to us". */}
          <div className="relative ml-auto shrink-0 sm:ml-0">
            <button
              ref={buttonRef}
              type="button"
              aria-expanded={open}
              aria-controls={menuId}
              onClick={() => setOpen((value) => !value)}
              className={`${chip} inline-flex items-center gap-1.5 ${capabilities.current || open ? 'wb-chosen' : idle}`}
            >
              {capabilities.label}
              <svg aria-hidden="true" viewBox="0 0 12 12" width="10" height="10" className={open ? 'rotate-180' : ''}>
                <path d="M2 4.5 6 8.5 10 4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Hidden, not unmounted, so aria-controls always names a real node.
                Hangs under its own button: right-aligned on a phone so it stays
                inside the viewport, left-aligned from sm up. */}
            <ul
              id={menuId}
              hidden={!open}
              aria-label={capabilities.label}
              className="absolute right-0 top-full z-[var(--wb-z-popover,50)] mt-2 w-[min(18rem,calc(100vw-2rem))] rounded-xl border border-white/[0.1] bg-[#0d0d0d] p-1.5 shadow-2xl sm:left-0 sm:right-auto"
            >
              {capabilities.items.map((item) => (
                <li key={item.slug}>
                  <Link
                    href={item.href}
                    prefetch
                    aria-current={item.current ? 'page' : undefined}
                    onClick={() => setOpen(false)}
                    className={`block rounded-lg border border-transparent px-3 py-2.5 text-[0.875rem] no-underline ${
                      item.current ? 'wb-chosen font-medium' : 'text-white/70 hover:bg-white/[0.05] hover:text-white'
                    }`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <Link
            href={cta.href}
            className="marketing-button-primary shrink-0 whitespace-nowrap !px-4 !py-2 !text-[0.8125rem] sm:ml-auto"
          >
            {cta.label}
          </Link>
        </div>
      </div>
    </nav>
  )
}
